// client/src/components/pages/LexiStar/PlanDispatch/usePlanReference.ts
//
// Reference data for planning: assets (M1 register, /lexistar/assets), lanes and routes (M2-A1,
// /lexistar/lanes, /lexistar/routes), sites (existing /locations) and — with `withDrivers`
// (ST-00198, the plan grid's driver picker) — the active drivers of the M3 driver register
// (/lexistar/drivers). Each list fails independently — a missing routes API leaves the route
// pickers empty with a note rather than breaking the page. Converted to TypeScript in ST-00198.

import { useEffect, useMemo, useState } from 'react';
import { listAssets } from '../../../../services/lexistarAssets';
import {
  listLanes,
  listRoutes,
  listSites,
  pickList,
} from '../../../../services/lexistarStatusBoard';
import { listActiveDrivers } from '../../../../services/lexistarTripDrivers';
import type { RegisterDriverRow } from '../../../../types/lexistar-trip-drivers';
import type { PlanAsset, PlanLane, PlanRoute, PlanSite } from './planTypes';

const ROAD_TRACTORS = ['horse', 'rigid'];

/** The reference lists that could not be loaded ('drivers' only with withDrivers). */
export type ReferenceList = 'assets' | 'lanes' | 'routes' | 'sites' | 'drivers';

interface ReferenceState {
  assets: PlanAsset[];
  lanes: PlanLane[];
  routes: PlanRoute[];
  sites: PlanSite[];
  drivers: RegisterDriverRow[];
  loading: boolean;
  failed: ReferenceList[];
}

export interface PlanReference extends ReferenceState {
  horses: PlanAsset[];
  trailers: PlanAsset[];
  assetsById: Map<string, PlanAsset>;
  routesById: Map<string, PlanRoute>;
  lanesById: Map<string, PlanLane>;
  driversById: Map<string, RegisterDriverRow>;
  siteName: (id: unknown) => string | null;
}

export default function usePlanReference({
  enabled = true,
  withDrivers = false,
}: { enabled?: boolean; withDrivers?: boolean } = {}): PlanReference {
  const [state, setState] = useState<ReferenceState>({
    assets: [],
    lanes: [],
    routes: [],
    sites: [],
    drivers: [],
    loading: true,
    failed: [],
  });

  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;
    Promise.allSettled([
      listAssets({ limit: 200 }), // the M1 API's page maximum
      listLanes(),
      listRoutes(),
      listSites(),
      withDrivers ? listActiveDrivers() : Promise.resolve([] as RegisterDriverRow[]),
    ]).then(([a, l, r, s, d]) => {
      if (!alive) return;
      const failed: ReferenceList[] = [];
      if (a.status === 'rejected') failed.push('assets');
      if (l.status === 'rejected') failed.push('lanes');
      if (r.status === 'rejected') failed.push('routes');
      if (s.status === 'rejected') failed.push('sites');
      if (d.status === 'rejected') failed.push('drivers');
      setState({
        assets: a.status === 'fulfilled' ? (pickList(a.value, 'assets') as PlanAsset[]) : [],
        lanes: l.status === 'fulfilled' ? (pickList(l.value, 'lanes') as PlanLane[]) : [],
        routes:
          r.status === 'fulfilled'
            ? (pickList(r.value, 'routes') as PlanRoute[]).filter((x) => x.status !== 'retired')
            : [],
        sites:
          s.status === 'fulfilled' ? (pickList(s.value, 'locations', 'sites') as PlanSite[]) : [],
        drivers: d.status === 'fulfilled' ? d.value.filter((x) => x.active !== false) : [],
        loading: false,
        failed,
      });
    });
    return () => {
      alive = false;
    };
  }, [enabled, withDrivers]);

  return useMemo(() => {
    const assetsById = new Map(state.assets.map((a) => [a.id, a]));
    const routesById = new Map(state.routes.map((r) => [r.id, r]));
    const lanesById = new Map(state.lanes.map((l) => [l.id, l]));
    const driversById = new Map(state.drivers.map((d) => [d.id, d]));
    // Lanes (M2-A1) name their ends from_location_name / to_location_name.
    const laneEnd = (l: PlanLane | undefined, end: 'from' | 'to'): string | null =>
      (end === 'from'
        ? l?.from_location_name || l?.from_name
        : l?.to_location_name || l?.to_name) || null;
    const siteName = (id: unknown): string | null =>
      state.sites.find((s) => String(s.location_id) === String(id))?.location_name ||
      laneEnd(
        state.lanes.find((l) => String(l.from_location_id) === String(id)),
        'from'
      ) ||
      laneEnd(
        state.lanes.find((l) => String(l.to_location_id) === String(id)),
        'to'
      ) ||
      null;
    return {
      ...state,
      horses: state.assets.filter((a) => ROAD_TRACTORS.includes(String(a.asset_type))),
      trailers: state.assets.filter((a) => a.asset_type === 'trailer'),
      assetsById,
      routesById,
      lanesById,
      driversById,
      siteName,
    };
  }, [state]);
}
